import { Button, Form, Select, Switch, TimePicker, message } from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import { FC } from "react";
import { trashSVG } from "../../../../assets/SVGs/SVGs";
import { getTimeOffTypes } from "../../../../network/timeOff";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryKeys } from "../../../../services/react-query/queryKeys";
import { TimeOffTypes } from "../../../../types/timeOffTypes";
import { getFormatedDayDate, getWeekDays } from "../../../../utils/DateUtils";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import arLocale from "antd/es/date-picker/locale/ar_EG";
import enLocale from "antd/es/date-picker/locale/en_US";
import { addBreak } from "../../../../network/workingHours";
import queryClient from "../../../../services/react-query/queryClient";
import { location } from "../../../../types/calendar";
import {
  adminAddBreakToShift,
  adminGetTimeOffTypes,
  getLocationDetailsAdmin,
} from "../../../../network/admin";

type shiftBreak = {
  from: string;
  to: string;
  approved: boolean;
  description?: string;
  typeId: number;
};
interface AddBreakFormProps {
  locationId?: number;
  teamMemberId?: number;
  shifts?: Array<{ id: number; date: string } | null>;
  index: number;
  breakIndex: number;
  shiftBreak?: shiftBreak;
  handleAddBreak: (
    index: number,
    breakIndex: number,
    shiftBreak: shiftBreak
  ) => void;
  handleClose: () => void;
  selectedDate: string;
  dayBreak?: { teamMemberId: number; locationId: number };
  handleDeleteBreak: (index: number, breakIndex: number) => void;
  config?: {
    isAdmin?: boolean;
    isEdit?: boolean;
    shiftId?: number;
    salonId?: number;
  };
}

const AddBreakForm: FC<AddBreakFormProps> = ({
  shiftBreak,
  teamMemberId,
  shifts,
  handleAddBreak,
  handleClose,
  index,
  breakIndex,
  dayBreak,
  selectedDate,
  handleDeleteBreak,
  locationId,
  config,
}) => {
  const intl = useIntl();
  const { currentLang, activeLocation } = useSelector(
    (state: {
      user: { currentLang: "en" | "ar" };
      calendar: { activeLocation: location | null };
    }) => {
      return {
        currentLang: state?.user?.currentLang,
        activeLocation: state.calendar.activeLocation,
      };
    }
  );
  const { data: locationDetails, isFetching } = useQuery(
    ["location", locationId],
    () => getLocationDetailsAdmin(locationId),
    {
      select: (res) => res.data,
      enabled: !!config?.isAdmin && !!locationId,
    }
  );

  let shiftId: number;
  if (dayBreak) {
    if (shifts) {
      const shift = shifts.find((item) => {
        return (
          item &&
          dayjs(item?.date).format("YYYY-MM-DD") ===
            dayjs(selectedDate).format("YYYY-MM-DD")
        );
      });

      if (shift) {
        shiftId = shift?.id;
      }
    } else {
      message.warning(
        intl.formatMessage({
          id: "thereIsNoShift",
          defaultMessage: "لا يوجد دوام في هذا اليوم",
        })
      );
      handleClose();
    }
  }
  const [form] = Form.useForm();
  const to = Form.useWatch("to", form);
  const from = Form.useWatch("from", form);
  const approved = Form.useWatch("approvedBreak", form);

  const { data: timeOffTypes, isLoading: timeOffTypesLoading } = useQuery(
    [queryKeys.timeOffTypes, config?.salonId],
    () => {
      if (config?.isAdmin) {
        return adminGetTimeOffTypes(config?.salonId);
      } else {
        return getTimeOffTypes();
      }
    }
  );
  const { mutate: onAddBreak, isLoading } = useMutation(
    (data: any) => {
      if (config?.isAdmin) {
        return adminAddBreakToShift(
          locationId,
          teamMemberId,
          config?.shiftId,
          data
        );
      } else {
        if (!shiftId) {
          return false;
        }
        return addBreak(locationId, teamMemberId, shiftId, data);
      }
    },
    {
      onSuccess: (res: any) => {
        if (res?.code !== "ERR_BAD_REQUEST") {
          queryClient.invalidateQueries([
            config?.isAdmin ? "TeamMember" : queryKeys.workingHours,
          ]);
          handleClose();
        }
      },
    }
  );
  const options: any = Array.isArray(timeOffTypes?.data)
    ? timeOffTypes?.data?.map((item: TimeOffTypes) => ({
        value: item.id,
        label: item.reason,
      }))
    : Array.isArray(timeOffTypes?.data?.data)
    ? timeOffTypes?.data?.data?.map((item: TimeOffTypes) => ({
        value: item.id,
        label: item.reason,
      }))
    : [];
  const onFinish = (values: any) => {
    const durationMinuts =
      (+dayjs(to).format("HH:mm").split(":")[0] -
        +dayjs(from).format("HH:mm").split(":")[0]) *
        60 +
      (+dayjs(to).format("HH:mm").split(":")[1] -
        +dayjs(from).format("HH:mm").split(":")[1]);
    if (durationMinuts < 1) {
      message.error(
        intl.formatMessage({
          id: "timeError",
          defaultMessage: "تاكد من الوقت المدخل",
        })
      );
      return;
    }
    const newShiftBreak = {
      from: dayjs(values?.from).format("HH:mm"),
      to: dayjs(values?.to).format("HH:mm"),
      approved: values?.approvedBreak,
      description: values?.description,
      typeId: +values?.typeId,
    };
    if (dayBreak) {
      const data = {
        from: dayjs(values?.from).format("HH:mm"),
        to: dayjs(values?.to).format("HH:mm"),
        approved: values?.approvedBreak,
        description: values?.description,
        type_Id: +values?.typeId,
        off: true,
      };

      onAddBreak(data);
    } else {
      handleAddBreak(index, breakIndex, newShiftBreak);
    }
  };
  const dayIndex = dayjs(selectedDate)?.day();
  const day = getWeekDays(new Date(selectedDate), "en")[dayIndex];
  const workingDay =
    config?.isAdmin && locationDetails
      ? locationDetails?.opening_hours?.find(
          (d: { day: string }) =>
            d?.day?.toLocaleLowerCase() === day?.day?.toLocaleLowerCase()
        )
      : activeLocation?.opening_hours?.find(
          (d) => d?.day?.toLocaleLowerCase() === day?.day?.toLocaleLowerCase()
        );

  return (
    <div className="space-y-4 m-4 shadow-full rounded-lg p-4">
      <div className="flex flex-col justify-center items-center space-y-3">
        <h3 className="text-color-primary text-lg">
          {intl.formatMessage({ id: "break", defaultMessage: "اضافة استراحة" })}
        </h3>
        {selectedDate ? (
          <p className="text-color-sec-title">
            {getFormatedDayDate(selectedDate, currentLang)}
          </p>
        ) : null}
      </div>
      <div className="my-2">
        <Form onFinish={onFinish} form={form} layout="vertical">
          {approved}
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row  justify-center md:justify-between gap-4">
              <div className="w-full md:w-1/3">
                {" "}
                <Form.Item
                  colon={false}
                  required={false}
                  label={intl.formatMessage({
                    id: "breakType",
                    defaultMessage: "طبيعة الاستراحة",
                  })}
                  name={"typeId"}
                  // rules={[
                  //   {
                  //     required: true,
                  //     message: intl.formatMessage({
                  //       id: "breakTypeRequired",
                  //       defaultMessage: "ادخل طبيعة الاستراحة",
                  //     }),
                  //   },
                  // ]}
                  initialValue={shiftBreak?.typeId || options[0]?.value}
                >
                  <Select
                    loading={timeOffTypesLoading}
                    defaultValue={shiftBreak?.typeId || options[0]?.value}
                    options={options}
                    defaultActiveFirstOption
                  />
                </Form.Item>
              </div>
              <div className="w-full md:w-2/3 flex gap-2">
                <Form.Item
                  colon={false}
                  required={false}
                  label={
                    <span className="text-color-primary">
                      {intl.formatMessage({
                        id: "from",
                        defaultMessage: "من",
                      })}
                    </span>
                  }
                  initialValue={dayjs(shiftBreak?.from || "00:00", "HH:mm")}
                  name={"from"}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "startTimeRequired",
                        defaultMessage: "حدد بداية الوقت",
                      }),
                    },
                  ]}
                >
                  <TimePicker
                    locale={currentLang === "ar" ? arLocale : enLocale}
                    placeholder={intl.formatMessage({
                      id: "from",
                      defaultMessage: "من",
                    })}
                    id="input-with-icon-adornment"
                    format={"hh:mm A"}
                    minuteStep={5}
                    needConfirm={false}
                    disabledTime={(time) => {
                      // const selectedStartTime = day.from;

                      const disabledHours = Array.from(
                        { length: 24 },
                        (_, hour) => hour
                      );

                      if (!workingDay) return {};
                      const startHour =
                        +workingDay?.opening_time?.split(":")[0];
                      const endHour = +workingDay?.closing_time?.split(":")[0];
                      const newDisabled = disabledHours?.filter(
                        (hour) => hour < +startHour || hour > +endHour
                      );
                      // const disabledMinutes = Array.from(
                      //   { length: 60 },
                      //   (_, minute) => minute
                      // );
                      // const startMinute = +selectedStartTime.split(":")[1];
                      // disabledMinutes.splice(startMinute, 60);
                      const disabled = {
                        disabledHours: () => newDisabled,
                        // disabledMinutes: () => disabledMinutes,
                      };

                      return disabled;
                    }}
                  />
                </Form.Item>
                <Form.Item
                  colon={false}
                  required={false}
                  label={
                    <span className="text-color-primary">
                      {intl.formatMessage({
                        id: "to",
                        defaultMessage: "الى",
                      })}
                    </span>
                  }
                  name={"to"}
                  initialValue={dayjs(shiftBreak?.to || "00:00", "HH:mm")}
                  rules={[
                    {
                      required: true,
                      message: intl.formatMessage({
                        id: "endTimeRequired",
                        defaultMessage: "حدد نهاية الوقت",
                      }),
                    },
                  ]}
                >
                  <TimePicker
                    locale={currentLang === "ar" ? arLocale : enLocale}
                    placeholder={intl.formatMessage({
                      id: "to",
                      defaultMessage: "الى",
                    })}
                    id="input-with-icon-adornment"
                    format={"hh:mm A"}
                    minuteStep={5}
                    disabledTime={() => {
                      const selectedStartTime = form.getFieldValue("from");
                      if (selectedStartTime) {
                        const disabledHours = Array.from(
                          { length: 24 },
                          (_, hour) => hour
                        );
                        const startHour = selectedStartTime?.hour();
                        if (!workingDay) return {};
                        const dayEndHour =
                          +workingDay?.closing_time?.split(":")[0];
                        const newDisabledHours = disabledHours?.filter(
                          (hour) => hour < startHour || hour > dayEndHour
                        );
                        const disabledMinutes = Array.from(
                          { length: 60 },
                          (_, minute) => minute
                        );
                        const startMinute = selectedStartTime?.minute();
                        disabledMinutes.splice(startMinute, 60);
                        return {
                          disabledHours: () => newDisabledHours,
                          disabledMinutes: () => disabledMinutes,
                        };
                      }
                      return {};
                    }}
                    needConfirm={false}
                  />
                </Form.Item>
              </div>
            </div>
            <div>
              <Form.Item
                colon={false}
                required={false}
                label={
                  <p className="text-color-primary">
                    {" "}
                    {intl.formatMessage({
                      id: "Description",
                      defaultMessage: "وصف",
                    })}{" "}
                    <small className="text-xs">
                      {intl.formatMessage({
                        id: "optional",
                        defaultMessage: "اختياري",
                      })}
                    </small>
                  </p>
                }
                name={"description"}
                initialValue={shiftBreak?.description || " "}
              >
                <TextArea
                  rows={3}
                  bordered={false}
                  className="shadow-full focus:ring hover:ring  ring-gray-100 rounded-lg p-3"
                />
              </Form.Item>
              <div className="flex justify-center items-baseline gap-2">
                <span
                  className={
                    !approved ? "text-color-primary" : "text-color-sec-title"
                  }
                >
                  {intl.formatMessage({
                    id: "nonApprovedBreak",
                    defaultMessage: "عطلة غير معتمدة",
                  })}
                </span>

                <Form.Item
                  name={"approvedBreak"}
                  valuePropName="checked"
                  colon={false}
                  required={false}
                  initialValue={!!shiftBreak?.approved}
                  labelAlign="left"
                  // label={"عطلة  معتمدة"}
                >
                  <Switch
                    defaultChecked={shiftBreak?.approved || true}
                    className="mx-2 w-[65px]"
                  />
                </Form.Item>
                <span
                  className={
                    approved ? "text-color-primary" : "text-color-sec-title"
                  }
                >
                  {intl.formatMessage({
                    id: "approvedBreak",
                    defaultMessage: "إستراحة معتمدة",
                  })}
                </span>
              </div>
            </div>
            <div className="bg-[#FBFBFF] p-3 ">
              <span className="bg-[#CF953D] h-[4px] w-[4px] rounded-full"></span>
              <span className="text-color-primary">
                {intl.formatMessage({
                  id: "breakDuration",
                  defaultMessage: "breakDuration",
                })}
                :{" "}
              </span>
              <span className="text-[#CF953D]">
                {/* {dayjs(from).format("HH:mm")} - {dayjs(to).format("HH:mm")} */}
                {to && from
                  ? (+dayjs(to).format("HH:mm").split(":")[0] -
                      +dayjs(from).format("HH:mm").split(":")[0]) *
                      60 +
                    (+dayjs(to).format("HH:mm").split(":")[1] -
                      +dayjs(from).format("HH:mm").split(":")[1])
                  : 0}
                <small className="text-xs">
                  {" "}
                  {intl.formatMessage({
                    id: "minutes",
                    defaultMessage: "دقيقة",
                  })}
                </small>
              </span>
            </div>
            <div className="flex justify-center   ">
              {shiftBreak ? (
                <Button
                  type="text"
                  htmlType="button"
                  onClick={() => handleDeleteBreak(index, breakIndex)}
                >
                  <span>{trashSVG}</span>
                  <span className="text-red-600">
                    {intl.formatMessage({
                      id: "deleteBreak",
                      defaultMessage: "حذف الإستراحه",
                    })}
                  </span>
                </Button>
              ) : null}
            </div>
            <div className="flex flex-col md:flex-row gap-2">
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={isLoading}
              >
                {intl.formatMessage({
                  id: "save",
                  defaultMessage: "حفظ",
                })}
              </Button>
              <Button
                type="default"
                htmlType="button"
                block
                onClick={handleClose}
              >
                {intl.formatMessage({
                  id: "cancel",
                  defaultMessage: "الغاء",
                })}
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AddBreakForm;
